import log from 'loglevel';
const merge = require('deepmerge');
//import merge from 'lodash/merge';
import { Html5 } from './providers/html5/Html5';
import { Hls } from './providers/hls/Hls';
import { Dash } from './providers/dash/Dash';
import { Whep } from './providers/webrtc/Whep';

import {
  InitOptions,
  State,
  QualityLevel,
  QualityLevelKind,
  TextTrack,
  AudioTrack,
  VideoTrack,
  Control,
  NotificationAction,
  AlternativeLanguageSource,
  Responsive,
  ResponsiveItem,
  PluginItem,
  CustomProvider,
  StreamType,
  Events,
  InitialValues,
  SourceType,
  PlaybackMode,
  ToggleControlsChangedEvents,
} from '../types/player';
import EventEmitter from '../utils/EventEmitter';
import ProviderInterface from './ProviderInterface';
import { PlayerEvents } from './PlayerEvents';
import { Options } from './optionsManager/Options';
import UserAgent from '../utils/UserAgent';
import PluginsManager from './plugins/PluginsManager';
import PluginInterface from './plugins/PluginInterface';
import PluginContainer from './plugins/PluginContainer';

let pI: PluginInterface; //we do that to be able to export this in d.ts

const PROVIDERS: any = {
  html5: Html5,
  hls: Hls,
  dash: Dash,
  whep: Whep,
};

/**
 * Description of Sdk class
 * @class Sdk
 * @classdesc This is a description of the MyClass class.
 * @hideconstructor
 */
class Sdk extends EventEmitter {
  currentProvider: Html5 | Hls | Dash | Whep;
  element: HTMLElement;
  currentState: State;
  userAgent: UserAgent = new UserAgent();
  _events: PlayerEvents = new PlayerEvents(this);
  _options: Options = new Options(this);
  size = '';
  /* we do it like for simulate real audio tracks */
  alternativeLanguageSources: AlternativeLanguageSource[] = [];
  alternativeLanguageSourcesAudioTrack: AudioTrack[] = [];
  hasAlternativeLanguageSources: boolean = false;
  currentAlternativeLanguageSourceIndex: number = 0;
  pluginsManager: PluginsManager = new PluginsManager(this);
  viewerLanguage: string | false = false;
  preventInitialValueAudioTrack: boolean = false;

  /**
   * Create sdk instance
   *
   * @hideconstructor
   */
  constructor(options: InitOptions) {
    super();
    this.currentProvider = null as any;
    this.element = null as any;
    this.options().setOptions(options);
    this.currentState = State.IDLE;
    this.options().onBeforeInit();
    this.on(Events.READY, () => {
      this.manageInitialValues();
    });
  }

  getUserAgent(): UserAgent {
    return this.userAgent;
  }

  options() {
    return this._options;
  }

  getClassNamePrefix(): string {
    return 'blastream';
  }

  async setContainerElement(element: HTMLElement) {
    this.element = element;
    let resizeObserver = new ResizeObserver(this.onResize.bind(this));
    resizeObserver.observe(this.element);
    this.onResize();
    this.element.addEventListener('mouseover', this.onMouseEnter.bind(this));
    this.element.addEventListener('mouseout', this.onMouseLeave.bind(this));
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this._events.playerVisible(entry.isIntersecting);
        });
      },
      { threshold: [0] },
    );

    observer.observe(this.element);
    await this.initializePlugins();
    this.initializeProvider();
  }

  async initializePlugins() {
    let plugins = this.options().getPlugins();
    for (var i in plugins) {
      let plugin: PluginItem = plugins[i];
      let pluginClass: any = plugin.pluginClass as PluginInterface;
      if (!pluginClass.Plugin) {
        pluginClass = { Plugin: pluginClass };
      }
      let pluginInstance = new pluginClass.Plugin(
        plugin.options,
        plugin.positions,
      );

      let pluginContainer: PluginContainer =
        this.pluginsManager.addPlugin(pluginInstance);
      await pluginInstance.init(this, pluginContainer);
    }
  }

  getPluginsManager(): PluginsManager {
    return this.pluginsManager;
  }

  async initializeProvider() {
    let sourceUrl = this.options().getSource()?.url || '';
    let provider = 'html5';
    if (
      sourceUrl.includes('.m3u8') ||
      this.options().getSource()?.streamType === StreamType.HLS
    ) {
      provider = 'hls';
    }
    if (
      sourceUrl.includes('.mpd') ||
      this.options().getSource()?.streamType === StreamType.DASH
    ) {
      provider = 'dash';
    }
    if (this.options().getSource()?.streamType === StreamType.WEBRTC) {
      provider = 'whep';
    }
    if (this.options().hasCustomProvider()) {
      let customProvider: CustomProvider = this.options().getProvider();

      let providerClass: any = customProvider.pluginClass as PluginInterface;
      if (!providerClass.Plugin) {
        providerClass = { Plugin: providerClass };
      }
      let providerPluginInstance = new providerClass.Plugin(
        customProvider.options,
      );

      //let providerPluginInstance = new window['BlastreamPlayerPlugin' + customProvider.name].Plugin(customProvider.options);
      this.currentProvider = providerPluginInstance.getProvider(
        this.element,
        this._options,
        this._events,
      );
      await providerPluginInstance.init(this);
    } else {
      this.currentProvider = new PROVIDERS[provider](
        this.element,
        this._options,
        this._events,
      );
    }
    if (this.options().getSourceType() === SourceType.ALTERNATIVE_LANGUAGE) {
      sourceUrl = this.processAlternativeAudioTracks();
    }
    //setTimeout(() => {
    this.currentProvider.init(sourceUrl);
    //}, 5000);
    this._events.isInit();
    log.debug('this.currentProvider', provider);
  }

  manageInitialValues() {
    let initialValues: InitialValues = this.options().getInitialValues();

    if (initialValues.autoSelectWithViewerLanguage) {
      let viewerLanguage = this.getViewerLanguage();
      let audioTracks = this.getAudioTracks();
      if(!this.preventInitialValueAudioTrack) {
        for (var i in audioTracks) {
          if (audioTracks[i].data?.lang === viewerLanguage || audioTracks[i].data?.code === viewerLanguage) {          
            this.setCurrentAudioTrack(parseInt(i));
            break;
          }
        }
      }

      let videoTracks = this.getVideoTracks();
      for (var i in videoTracks) {
        if (videoTracks[i].data?.lang === viewerLanguage) {
          this.setCurrentVideoTrack(parseInt(i));
          break;
        }
      }

      let textTracks = this.getTextTracks();
      for (var i in textTracks) {
        if (
          textTracks[i].srclang === viewerLanguage ||
          textTracks[i].data?.lang === viewerLanguage
        ) {
          this.setCurrentTextTrack(parseInt(i));
          break;
        }
      }
    }

    if (typeof initialValues.volume === 'number') {
      this.setVolume(initialValues.volume);
    }

    if (typeof initialValues.qualityLevelAudioIndex === 'number') {
      this.setCurrentQualityLevel(
        initialValues.qualityLevelAudioIndex,
        QualityLevelKind.AUDIO,
      );
    }

    if (typeof initialValues.qualityLevelVideoIndex === 'number') {
      this.setCurrentQualityLevel(
        initialValues.qualityLevelVideoIndex,
        QualityLevelKind.VIDEO,
      );
    }

    if (typeof initialValues.textTrackIndex === 'number') {
      this.setCurrentTextTrack(initialValues.textTrackIndex);
    }

    if (typeof initialValues.audioTrackIndex === 'number' && !this.preventInitialValueAudioTrack) {      
      this.setCurrentAudioTrack(initialValues.audioTrackIndex);
    }

    if (typeof initialValues.videoTrackIndex === 'number') {
      this.setCurrentVideoTrack(initialValues.videoTrackIndex);
    }

    console.log(
      'ready',
      this.getAudioTracks(),
      this.getTextTracks(),
      this.getVideoTracks(),
    );
  }

  updateOptions(options: InitOptions) {
    let oldOptions = this.options().getOptions();
    let updateSource = false;
    if (
      options.source &&
      options.sourceType &&
      (JSON.stringify(options.source) !==
        JSON.stringify(this.options().getSource()) ||
        options.sourceType !== this.options().getSourceType())
    ) {
      updateSource = true;
      this.currentProvider.stop();
    }
    const overwriteMerge = (
      destinationArray: any,
      sourceArray: any,
      options: any,
    ) => sourceArray;
    let newOptions = merge(oldOptions, options, { arrayMerge: overwriteMerge });
    console.log('options', updateSource, oldOptions, options, newOptions);
    this.options().setOptions(newOptions);
    if (updateSource) {
      this.initializeProvider();
    }
  }

  processAlternativeAudioTracks(): string {
    let sources = this.options().getAlternativeLanguageSources();
    let sourceUrl = '';
    let currentIndex: number = 0;
    if (sources) {
      if (this.currentProvider instanceof Hls) {
        this.currentProvider.onLevelAudioTrackSwitched = (index: number) => {
          console.log('do nothing');
        };
      }
      this.hasAlternativeLanguageSources = true;
      this.alternativeLanguageSources = sources;
      sourceUrl = sources[0].url;
      for (var i in sources) {
        this.alternativeLanguageSourcesAudioTrack.push({
          index: parseInt(i) as number,
          label: sources[i].language,
          data: {
            code: sources[i].code,
          },
        });
        if (sources[i].default) {
          sourceUrl = sources[i].url;
          currentIndex = parseInt(i) as number;
        }
      }
    }
    this.currentAlternativeLanguageSourceIndex = currentIndex;
    this._events.audioTrackChanged(this.getCurrentAudioTrack(), currentIndex);
    return sourceUrl;
  }

  getCurrentProvider(): ProviderInterface {
    return this.currentProvider;
  }

  /**
   * Method Play
   * @description play the video
   * @returns {void}
   * @example
   * ```js
   * player.play();
   * ```
   */
  play(): void {
    this.currentProvider.play();
  }

  /**
   * Method pause
   * @description put the video in pause
   * @returns {void}
   * @example
   * ```js
   * player.pause();
   * ```
   */
  pause() {
    this.currentProvider.pause();
  }

  /**
   * Method getCurrentState
   * @description get the current state of the video
   * @returns {State}
   * @example
   * ```js
   * player.getCurrentState();
   * ```
   */
  getCurrentState(): State {
    if (!this.currentProvider) return State.IDLE;
    return this.currentProvider.currentState;
  }

  /**
   * Method seek
   * @description seek the video to a specific time in seconds
   * @param {number} time - time in seconds
   * @returns {void}
   * @example
   * ```js
   * player.seek(15);
   * ```
   */
  seek(time: number) {
    this.currentProvider.seek(time);
  }

  /**
   * Method getCurrentTime
   * @description get the current time of the video
   * @returns {number}
   * @example
   * ```js
   * player.getCurrentTime();
   * ```
   */
  getCurrentTime(): number {
    return this.currentProvider.getCurrentTime();
  }

  /**
   * Method getDuration
   * @description get the duration of the video
   * @returns {number}
   * @example
   * ```js
   * player.getDuration();
   * ```
   */
  getDuration(): number {
    return this.currentProvider.getDuration();
  }

  /**
   * Method setLoop
   * @description set if video is in loop
   * @param {boolean} loop - loop the video
   * @returns {void}
   * @example
   * ```js
   * player.setLoop(true);
   * ```
   */
  setLoop(loop: boolean) {
    this.currentProvider.setLoop(loop);
  }

  /**
   * Method isLoop
   * @description if the video is in loop
   * @returns {boolean}
   * @example
   * ```js
   * player.isLoop();
   * ```
   */
  isLoop(): boolean {
    return this.currentProvider.isLoop();
  }

  /**
   * Method getVolume
   * @description get the volume of the video between 0 and 1
   * @returns {number}
   * @example
   * ```js
   * player.getVolume();
   * ```
   */
  getVolume(): number {
    return this.currentProvider.getVolume();
  }

  /**
   * Method setVolume
   * @description set the volume of the video between 0 and 1
   * @param {number} volume - volume between 0 and 1
   * @returns {void}
   * @example
   * player.setVolume(0.5);
   */
  setVolume(volume: number) {
    this.currentProvider.setVolume(volume);
  }

  /**
   * Method isMuted
   * @description if the audio is muted
   * @returns {boolean}
   * @example
   * ```js
   * player.isMuted();
   * ```
   */
  isMuted(): boolean {
    return this.currentProvider.isMuted();
  }

  /**
   * Method toggleMute
   * @description toggle the audio
   * @returns {void}
   * @example
   * ```js
   * player.toggleMute();
   * ```
   */
  toggleMute() {
    this.currentProvider.toggleMute();
  }

  /**
   * Method isLive
   * @description if the video is a livestreaming
   * @returns {boolean}
   * @example
   * ```js
   * player.isLive();
   * ```
   */
  isLive(): boolean {
    return this.currentProvider.isLive();
  }

  /**
   * Method isDvr
   * @description if the video is a livestreaming with DVR
   * @returns {boolean}
   * @example
   * ```js
   * player.isDvr();
   * ```
   */
  isDvr(): boolean {
    return this.currentProvider.isDvr();
  }

  /**
   * Method isDvrIsLive
   * @description if the viewer is watching the livestreaming or viewer went back in time
   * @returns {boolean}
   * @example
   * ```js
   * player.isDvrIsLive();
   * ```
   */
  isDvrIsLive() {
    return this.currentProvider.isDvrIsLive();
  }

  /**
   * Method hasQualityLevels
   * @param {QualityLevelKind} [kind=QualityLevelKind.VIDEO] - Kind of quality level (video, audio). Defaults to `QualityLevelKind.VIDEO`.
   * @description if the video has quality levels
   * @returns {boolean}
   * @example
   * ```js
   * player.hasQualityLevels();
   * ```
   */
  hasQualityLevels(kind: QualityLevelKind = QualityLevelKind.VIDEO): boolean {
    return this.currentProvider.hasQualityLevels(kind);
  }

  /**
   * Method getQualityLevels
   * @param {QualityLevelKind} [kind=QualityLevelKind.VIDEO] - Kind of quality level (video, audio). Defaults to `QualityLevelKind.VIDEO`.
   * @description get the quality levels of the video
   * @returns {QualityLevel[]}
   * @example
   * ```js
   * player.getQualityLevels();
   * ```
   */
  getQualityLevels(
    kind: QualityLevelKind = QualityLevelKind.VIDEO,
  ): QualityLevel[] {
    return this.currentProvider.getQualityLevels(kind);
  }

  /**
   * Method setCurrentQualityLevel
   * @description set the current quality level of the video, put -1 for auto quality level
   * @param {number} index - index of the quality level
   * @param {QualityLevelKind} [kind=QualityLevelKind.VIDEO] - Kind of quality level (video, audio). Defaults to `QualityLevelKind.VIDEO`.
   * @returns {QualityLevel}
   * @example
   * ```js
   * player.setCurrentQualityLevel(1, 1);
   * ```
   */
  setCurrentQualityLevel(
    index: number,
    kind: QualityLevelKind = QualityLevelKind.VIDEO,
  ): QualityLevel {
    return this.currentProvider.setCurrentQualityLevel(index, kind);
  }

  /**
   * Method isAutoQualityLevel
   * @description if the video is in auto quality level
   * @param {QualityLevelKind} [kind=QualityLevelKind.VIDEO] - Kind of quality level (video, audio). Defaults to `QualityLevelKind.VIDEO`.
   * @returns {boolean}
   * @example
   * ```js
   * player.isAutoQualityLevel();
   * ```
   */
  isAutoQualityLevel(kind: QualityLevelKind = QualityLevelKind.VIDEO): boolean {
    return this.currentProvider.isAutoQualityLevel(kind);
  }

  /**
   * Method isAutoQualityLevelAvailable
   * @param {QualityLevelKind} [kind=QualityLevelKind.VIDEO] - Kind of quality level (video, audio). Defaults to `QualityLevelKind.VIDEO`.
   * @description if the video has auto quality level
   * @returns {boolean}
   * @example
   * ```js
   * player.isAutoQualityLevelAvailable('video');
   * ```
   */
  isAutoQualityLevelAvailable(
    kind: QualityLevelKind = QualityLevelKind.VIDEO,
  ): boolean {
    return this.currentProvider.isAutoQualityLevelAvailable(kind);
  }

  /**
   * Method getCurrentQualityLevel
   * @param {QualityLevelKind} [kind=QualityLevelKind.VIDEO] - Kind of quality level (video, audio). Defaults to `QualityLevelKind.VIDEO`.
   * @description get the current quality level of the video
   * @returns {QualityLevel}
   * @example
   * ```js
   * player.getCurrentQualityLevel('video');
   * ```
   */
  getCurrentQualityLevel(
    kind: QualityLevelKind = QualityLevelKind.VIDEO,
  ): QualityLevel {
    return this.currentProvider.getCurrentQualityLevel(kind);
  }

  /**
   * Method hasTextTracks
   * @description if the video has text tracks
   * @returns {boolean}
   * @example
   * ```js
   * player.hasTextTracks();
   * ```
   */
  hasTextTracks(): boolean {
    return this.currentProvider.hasTextTracks();
  }

  /**
   * Method getTextTracks
   * @description get the text tracks of the video
   * @returns {TextTrack[]}
   * @example
   * ```js
   * player.getTextTracks();
   * ```
   */
  getTextTracks(): TextTrack[] {
    return this.currentProvider.getTextTracks();
  }

  /**
   * Method setCurrentTextTrack
   * @description set the current text track of the video
   * @param {number} index - index of the text track
   * @returns {TextTrack}
   * @example
   * ```js
   * player.setCurrentTextTrack(1);
   * ```
   */
  setCurrentTextTrack(index: number): TextTrack {
    return this.currentProvider.setCurrentTextTrack(index);
  }

  /**
   * Method getCurrentTextTrack
   * @description get the current text track of the video
   * @returns {TextTrack}
   * @example
   * ```js
   * player.getCurrentTextTrack();
   * ```
   */
  getCurrentTextTrack(): TextTrack {
    return this.currentProvider.getCurrentTextTrack();
  }

  /**
   * Method addTextTrack
   * @description add a text track to the video
   * @param {TextTrack} textTrack - text track to add
   * @returns {number} index of the text track
   * @example
   * ```js
   * player.addTextTrack({
   *   label: 'English',
   *   src: 'https://example.com/english.vtt',
   *   srclang: 'en',
   *   kind: 'subtitles',
   * });
   * ```
   */
  addTextTrack(textTrack: TextTrack): number {
    return this.currentProvider.addTextTrack(textTrack);
  }

  /**
   * Method hasAudioTracks
   * @description if the video has audio tracks
   * @returns {boolean}
   * @example
   * ```js
   * player.hasAudioTracks();
   * ```
   */
  hasAudioTracks(): boolean {
    if (this.hasAlternativeLanguageSources) {
      return this.alternativeLanguageSourcesAudioTrack.length > 1;
    }
    return this.currentProvider.hasAudioTracks();
  }

  /**
   * Method getAudioTracks
   * @description get the audio tracks of the video
   * @returns {AudioTrack[]}
   * @example
   * ```js
   * player.getAudioTracks();
   * ```
   */
  getAudioTracks(): AudioTrack[] {
    if (this.hasAlternativeLanguageSources) {
      return this.alternativeLanguageSourcesAudioTrack;
    }
    return this.currentProvider.getAudioTracks();
  }

  /**
   * Method setCurrentAudioTrack
   * @description set the current audio track of the video
   * @param {number} index - index of the audio track
   * @returns {AudioTrack}
   * @example
   * ```js
   * player.setCurrentAudioTrack(1);
   * ```
   */
  setCurrentAudioTrack(index: number): AudioTrack {
    if (this.hasAlternativeLanguageSources) {
      this.currentAlternativeLanguageSourceIndex = index;
      let alternativeLanguageSource = this.alternativeLanguageSources[
        index
      ] as AlternativeLanguageSource;
      let currentTime = this.getCurrentTime();
      let state: State = this.getCurrentState();
      //this.pause();
      //this.destroy();
      this.preventInitialValueAudioTrack = true;
      this.currentProvider._readySended = false;
      this.currentProvider.init(alternativeLanguageSource.url);
      if(this.currentProvider instanceof Hls) 
        this.currentProvider.hlsOptions.preventLoopBack = false;
      //this.initializeProvider();
      this.once(Events.READY, () => {
        if (this.options().getSource().playbackMode !== PlaybackMode.LIVE) {
          this.seek(currentTime);
          this.on(Events.SEEKED, () => {
            if (state === State.PLAYING) this.play();
            this.onResize();
          });
        } else {
          this.play();
          this.onResize();
        }
        this.preventInitialValueAudioTrack = false;
        if(this.currentProvider instanceof Hls) 
          this.currentProvider.hlsOptions.preventLoopBack = true;
      });
      console.log('setCurrentAudioTrack index', index);
      this._events.audioTrackChanged(this.getCurrentAudioTrack(), index);
      this.options().getControls().refresh();
      return this.currentProvider.getCurrentAudioTrack();
    } else {
      return this.currentProvider.setCurrentAudioTrack(index);
    }
  }

  /**
   * Method getCurrentAudioTrack
   * @description get the current audio track of the video
   * @returns {AudioTrack}
   * @example
   * ```js
   * player.getCurrentAudioTrack();
   * ```
   */
  getCurrentAudioTrack(): AudioTrack {
    if (this.hasAlternativeLanguageSources) {
      return this.alternativeLanguageSourcesAudioTrack[
        this.currentAlternativeLanguageSourceIndex
      ];
    }
    return this.currentProvider.getCurrentAudioTrack();
  }

  /**
   * Method hasVideoTracks
   * @description if the video has video tracks
   * @returns {boolean}
   * @example
   * ```js
   * player.hasVideoTracks();
   * ```
   */
  hasVideoTracks(): boolean {
    return this.currentProvider.hasVideoTracks();
  }

  /**
   * Method getVideoTracks
   * @description get the video tracks of the video
   * @returns {VideoTrack[]}
   * @example
   * ```js
   * player.getVideoTracks();
   * ```
   */
  getVideoTracks(): VideoTrack[] {
    return this.currentProvider.getVideoTracks();
  }

  /**
   * Method setCurrentVideoTrack
   * @description set the current video track of the video
   * @param {number} index - index of the video track
   * @returns {VideoTrack}
   * @example
   * ```js
   * player.setCurrentVideoTrack(1);
   * ```
   */
  setCurrentVideoTrack(index: number): VideoTrack {
    return this.currentProvider.setCurrentVideoTrack(index);
  }

  /**
   * Method getCurrentVideoTrack
   * @description get the current video track of the video
   * @returns {VideoTrack}
   * @example
   * ```js
   * player.getCurrentVideoTrack();
   * ```
   */
  getCurrentVideoTrack(): VideoTrack {
    return this.currentProvider.getCurrentVideoTrack();
  }

  /**
   * Method hasPlaybackRates
   * @description if the video has playback rates
   * @returns {boolean}
   * @example
   * ```js
   * player.hasPlaybackRates();
   * ```
   */
  hasPlaybackRates(): boolean {
    return this.currentProvider.hasPlaybackRates();
  }

  /**
   * Method getPlaybackRates
   * @description get the playback rates of the video
   * @returns {number[]}
   * @example
   * ```js
   * player.getPlaybackRates();
   * ```
   */
  getPlaybackRates(): number[] {
    return this.currentProvider.getPlaybackRates();
  }

  /**
   * Method getCurrentPlaybackRate
   * @description get the current playback rate of the video
   * @returns {number}
   * @example
   * ```js
   * player.getCurrentPlaybackRate();
   * ```
   */
  getCurrentPlaybackRate(): number {
    return this.currentProvider.getCurrentPlaybackRate();
  }

  getSeekControlInterval(): number {
    let seekInterval = this.options()
      .getControls()
      .getSettings().seekControlInterval;
    return seekInterval ?? 0;
  }

  /**
   * Method seekRewind
   * @description seek the video backward
   * @returns {void}
   * @example
   * ```js
   * player.seekRewind();
   * ```
   */
  seekRewind() {
    const seek = this.getCurrentTime() - this.getSeekControlInterval();
    this.seek(seek);
  }

  /**
   * Method seekForward
   * @description seek the video forward
   * @returns {void}
   * @example
   * ```js
   * player.seekForward();
   * ```
   */
  seekForward() {
    const seek = this.getCurrentTime() + this.getSeekControlInterval();
    this.seek(seek);
  }

  /**
   * Method setSeekControlInterval
   * @description set the seek control interval
   * @param {number} interval - interval in seconds
   * @returns {void}
   * @example
   * ```js
   * player.setSeekControlInterval(10);
   * ```
   */
  setSeekControlInterval(interval: number) {
    if (
      this.options().getControls().getSettings().seekControlInterval ===
      undefined
    )
      return;
    this.options().getControls().getSettings().seekControlInterval = interval;
    this._events.seekControlIntervalChanged(interval);
  }

  /**
   * Method setPlaybackRate
   * @description set the playback rate of the video
   * @param {number} rate - rate of the video
   * @returns {void}
   * @example
   * ```js
   * player.setPlaybackRate(1.5);
   * ```
   */
  setCurrentPlaybackRate(rate: number): void {
    this.currentProvider.setCurrentPlaybackRate(rate);
  }

  /**
   * Method toggleFullScreen
   * @description activate or desactivate the fullscreen
   * @returns {void}
   * @example
   * ```js
   * player.toggleFullScreen();
   * ```
   */
  toggleFullScreen() {
    this.currentProvider.toggleFullScreen(this.userAgent);
  }

  /**
   * Method toggleControls
   * @description show or hide controls on the video
   * @returns {void}
   * @example
   * ```js
   * player.toggleControls();
   * ```
   */
  toggleControls() {
    this.currentProvider.toggleControls();
  }

  /**
   * Method togglePip
   * @description activate or desactivate the picture in picture
   * @returns {void}
   * @example
   * ```js
   * player.togglePip();
   * ```
   */
  togglePip() {
    return this.currentProvider.togglePip();
  }

  /**
   * Method hasPip
   * @description if the video has picture in picture
   * @returns {boolean}
   * @example
   * ```js
   * player.hasPip();
   * ```
   */
  hasPip(): boolean {
    return this.currentProvider.hasPip();
  }

  /**
   * Method hasAirPlay
   * @description if air play is available
   * @returns {boolean}
   * @example
   * ```js
   * player.hasAirPlay();
   * ```
   */
  hasAirPlay(): boolean {
    return this.currentProvider.hasAirPlay();
  }

  showAirPlayTargetPicker() {
    return this.currentProvider.showAirPlayTargetPicker();
  }

  /**
   * Method destroy
   * @description destroy the player
   * @returns {void}
   * @example
   * ```js
   * player.destroy();
   * ```
   */
  destroy() {
    console.log('Blastream Player', 'destroy');
    this._events.destroy();
    this.currentProvider.destroy();
    this.pluginsManager.destroyPlugins();
  }

  notificationAction(action: NotificationAction): void {
    this.currentProvider.notificationAction(action);
  }

  toggleControlMenu(control: string): void {
    this.currentProvider.toggleControlMenu(control);
  }

  clickControl(control: string): void {
    this._events.clickControl(control);
  }

  toggleControlsChanged(status: ToggleControlsChangedEvents): void {
    this.currentProvider.toggleControlsChanged(status);
  }

  getControlsStatus(): ToggleControlsChangedEvents {
    return this.currentProvider.getControlsStatus();
  }
  /**
   * Method isStarted
   * @description if the video is started
   * @returns {boolean}
   * @example
   * ```js
   * player.isStarted();
   * ```
   */
  isStarted(): boolean {
    return this.currentProvider.isStarted();
  }

  /**
   * Method getViewerLanguage
   * @description get the viewer language
   * @returns {string | false}
   * @example
   * ```js
   * player.getViewerLanguage();
   * ```
   */
  getViewerLanguage(): string | false {
    if (this.viewerLanguage) return this.viewerLanguage;
    let lang;
    let locale = this.options().getLocale();
    if (locale) lang = locale.lang;
    if (!lang && this.options().getLocale()?.automatic_detection) {
      const browserLang = navigator.language || navigator.languages[0];
      if (browserLang) lang = browserLang.substring(0, 2);
    }
    if (!lang) return false;
    this.viewerLanguage = lang;
    return this.viewerLanguage;
  }

  getLibrary(): any {
    return this.currentProvider.getLibrary();
  }

  getSize(): string {
    return this.size;
  }

  onResize() {
    const responsive: Responsive = this.options().getResponsive() as Responsive;
    if (!responsive) return;

    //sort responsive by width
    responsive.sort((a: ResponsiveItem, b: ResponsiveItem) => {
      return a.width - b.width;
    });

    let newSize = '';
    let playerWidth = this.element.offsetWidth;
    let playerHeight = this.element.offsetHeight;

    if (this.options().getRatio() != -1) {
      let containerRatio = playerWidth / playerHeight;
      if (containerRatio < this.options().getRatio()) {
        playerHeight = playerWidth / this.options().getRatio();
      }
    }

    let finded = false;
    for (const size of responsive) {
      //console.log('size ici compare', size.width, playerWidth)
      if (!finded && playerWidth <= size.width) {
        newSize = size.className;
        finded = true;
      }
    }

    let refresh = false;
    if (newSize != this.size) {
      refresh = true;
      this.size = newSize;
    }

    this._events.resize(this.size, playerWidth, playerHeight);

    if (refresh) {
      this.options().getControls().refresh();
    }
  }

  onMouseEnter() {
    this._events.mouseOver(true);
  }
  onMouseLeave() {
    this._events.mouseOver(false);
  }
  setBigButton(button: string | null) {
    this._events.bigButtonChanged(button);
  }
}

export default Sdk;
